import { cap, redactText, redactUrl, redactValue } from "./redact";

/**
 * Pin feedback's client log: two small ring buffers installed once at app start (main.tsx) and read when a report is
 * filed. Everything is redacted on the way in, so a secret never sits in memory in a buffer either.
 *
 * - console: console.error / console.warn calls, uncaught errors and unhandled promise rejections;
 * - apiCalls: every same-origin /api request through window.fetch: method, path (secret query values redacted),
 *   status, duration, the server's X-Correlation-Id, and problem details on a failure. NEVER a request or response body
 *   otherwise, and never a header.
 *
 * Installing never changes what the app sees: the original console methods and fetch still run, and any failure inside
 * the recorder is swallowed.
 */
export interface ConsoleEntry {
  at: string;
  level: "error" | "warn";
  message: string;
}

export interface ApiCallEntry {
  at: string;
  method: string;
  path: string;
  status: number;
  durationMs: number;
  correlationId: string | null;
  problem?: Record<string, unknown>;
  error?: string;
}

export const CONSOLE_CAPACITY = 50;
export const API_CAPACITY = 50;
const MESSAGE_MAX = 2000;

export class RingBuffer<T> {
  private readonly items: T[] = [];
  constructor(readonly capacity: number) {}

  push(item: T): void {
    this.items.push(item);
    if (this.items.length > this.capacity) this.items.splice(0, this.items.length - this.capacity);
  }

  snapshot(): T[] {
    return [...this.items];
  }

  clear(): void {
    this.items.length = 0;
  }

  get size(): number {
    return this.items.length;
  }
}

const consoleLog = new RingBuffer<ConsoleEntry>(CONSOLE_CAPACITY);
const apiLog = new RingBuffer<ApiCallEntry>(API_CAPACITY);

function describe(value: unknown): string {
  if (value instanceof Error) return `${value.name}: ${value.message}${value.stack ? `\n${value.stack}` : ""}`;
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(redactValue(value));
  } catch {
    return String(value);
  }
}

export function recordConsole(level: ConsoleEntry["level"], args: unknown[]): void {
  try {
    consoleLog.push({ at: new Date().toISOString(), level, message: redactText(cap(args.map(describe).join(" "), MESSAGE_MAX * 2), MESSAGE_MAX) });
  } catch {
    // never let the recorder break the page
  }
}

/** Records one call; `response` null for a network failure. Problem details are read from a clone, asynchronously. */
export function recordApiCall(method: string, url: string, started: number, response: Response | null, error?: unknown): void {
  try {
    const entry: ApiCallEntry = {
      at: new Date().toISOString(),
      method: method.toUpperCase(),
      path: redactUrl(url).replace(/^https?:\/\/[^/]+/, ""),
      status: response?.status ?? 0,
      durationMs: Math.round(performance.now() - started),
      correlationId: response?.headers.get("X-Correlation-Id") ?? null,
    };
    if (!response) entry.error = redactText(describe(error), 300);
    apiLog.push(entry);
    if (response && !response.ok && (response.headers.get("Content-Type") ?? "").includes("json")) {
      void response
        .clone()
        .json()
        .then((body: unknown) => {
          if (body && typeof body === "object") {
            const { code, title, detail, status, errors } = body as Record<string, unknown>;
            entry.problem = redactValue({ code, title, detail, status, errors: Array.isArray(errors) ? errors.slice(0, 5) : undefined }) as Record<string, unknown>;
          }
        })
        .catch(() => undefined);
    }
  } catch {
    // never let the recorder break the page
  }
}

function isApiUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin && parsed.pathname.startsWith("/api/");
  } catch {
    return false;
  }
}

let uninstall: (() => void) | null = null;

/** Idempotent. Returns a function that restores the originals (tests). */
export function installDiagnostics(): () => void {
  if (uninstall) return uninstall;
  const originalError = console.error;
  const originalWarn = console.warn;
  const originalFetch = Reflect.get(window, "fetch") as typeof window.fetch | undefined;

  console.error = (...args: unknown[]) => {
    recordConsole("error", args);
    originalError.apply(console, args);
  };
  console.warn = (...args: unknown[]) => {
    recordConsole("warn", args);
    originalWarn.apply(console, args);
  };
  const onError = (event: ErrorEvent) => recordConsole("error", [`Uncaught ${event.error instanceof Error ? describe(event.error) : event.message}`, `at ${event.filename}:${event.lineno}:${event.colno}`]);
  const onRejection = (event: PromiseRejectionEvent) => recordConsole("error", ["Unhandled promise rejection:", event.reason]);
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);

  if (typeof originalFetch === "function") {
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = input instanceof Request ? input.url : String(input);
      const method = init?.method ?? (input instanceof Request ? input.method : "GET");
      if (!isApiUrl(url)) return originalFetch.call(window, input, init);
      const started = performance.now();
      try {
        const response = await originalFetch.call(window, input, init);
        recordApiCall(method, url, started, response);
        return response;
      } catch (error) {
        recordApiCall(method, url, started, null, error);
        throw error;
      }
    };
  }

  uninstall = () => {
    console.error = originalError;
    console.warn = originalWarn;
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
    if (typeof originalFetch === "function") Reflect.set(window, "fetch", originalFetch);
    uninstall = null;
  };
  return uninstall;
}

export function diagnosticsSnapshot(): { console: ConsoleEntry[]; apiCalls: ApiCallEntry[] } {
  return { console: consoleLog.snapshot(), apiCalls: apiLog.snapshot().map((entry) => ({ ...entry })) };
}

/** Tests only. */
export function clearDiagnostics(): void {
  consoleLog.clear();
  apiLog.clear();
}
